/* VINTAGE SCUBA — product page: swipe gallery + full-screen viewer, variants,
   AJAX add-to-cart (opens the cart drawer), sticky bar, tabs, wishlist label, share.
   Lightweight, no dependencies. */
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const $ = (s, c = document) => c.querySelector(s);
const $$ = (s, c = document) => [...c.querySelectorAll(s)];
const pad = (n) => String(n).padStart(2, '0');

function money(cents) {
  const f = (window.VS && window.VS.money) || '₹{{amount}}';
  const n = cents / 100;
  const whole = Math.round(n) === n;
  const amt = whole ? n.toLocaleString('en-IN') : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return f.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => (k.includes('no_decimals') ? Math.round(n).toLocaleString('en-IN') : amt));
}

/* index of the slide currently in view for a horizontal scroll-snap list */
function indexOf(track, count) {
  const w = track.clientWidth || 1;
  return Math.max(0, Math.min(count - 1, Math.round(track.scrollLeft / w)));
}
function jump(track, i, smooth) {
  const w = track.clientWidth;
  track.scrollTo({ left: i * w, behavior: smooth ? 'smooth' : 'auto' });
}

function initGallery(root) {
  const slider = $('[data-vs-slider]', root);
  if (!slider) return;
  const slides = $$('[data-slide]', slider);
  const thumbs = $$('[data-thumb]', root);
  const dots = $$('[data-dot]', root);
  const count = $('[data-vs-counter]', root);
  const prev = $('[data-vs-prev]', root);
  const next = $('[data-vs-next]', root);
  const total = slides.length;
  let idx = 0;
  let raf = 0;

  const paint = (i) => {
    idx = i;
    thumbs.forEach((t, k) => t.classList.toggle('is-on', k === i));
    dots.forEach((d, k) => d.classList.toggle('is-on', k === i));
    if (count) count.textContent = `${pad(i + 1)} / ${pad(total)}`;
    if (prev) prev.disabled = i === 0;
    if (next) next.disabled = i === total - 1;
    const th = thumbs[i];
    if (th && th.parentElement && th.closest('.vs-pdp__thumbs')) {
      const strip = th.closest('.vs-pdp__thumbs');
      const li = th.parentElement;
      const target = li.offsetLeft - (strip.clientWidth - li.clientWidth) / 2;
      strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }
  };
  slider.addEventListener('scroll', () => {
    if (raf) return;
    raf = requestAnimationFrame(() => { raf = 0; const i = indexOf(slider, total); if (i !== idx) paint(i); });
  }, { passive: true });

  const go = (i, smooth = true) => jump(slider, Math.max(0, Math.min(total - 1, i)), smooth);
  thumbs.forEach((t) => t.addEventListener('click', () => go(+t.dataset.thumb)));
  prev && prev.addEventListener('click', () => go(idx - 1));
  next && next.addEventListener('click', () => go(idx + 1));
  slider.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); go(idx + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(idx - 1); }
  });
  // keep the same photo in view when the phone rotates / window resizes
  window.addEventListener('resize', () => go(idx, false), { passive: true });
  paint(0);

  root.__vsGo = (imageId) => {
    const i = slides.findIndex((s) => String(s.dataset.imageId) === String(imageId));
    if (i > -1) go(i);
  };

  /* ---------- full-screen viewer ---------- */
  const viewer = $('[data-vs-viewer]', root);
  if (!viewer || typeof viewer.showModal !== 'function') return;
  const track = $('[data-viewer-track]', viewer);
  const vSlides = $$('.vs-viewer__slide', track);
  const vCount = $('[data-viewer-count]', viewer);
  const vPrev = $('[data-viewer-prev]', viewer);
  const vNext = $('[data-viewer-next]', viewer);
  let vIdx = 0;
  let vRaf = 0;
  let pushed = false;

  const load = (i) => {
    [i - 1, i, i + 1].forEach((k) => {
      const img = vSlides[k] && $('img', vSlides[k]);
      if (img && !img.src && img.dataset.src) img.src = img.dataset.src;
    });
  };
  const vPaint = (i) => {
    vIdx = i;
    if (vCount) vCount.textContent = `${pad(i + 1)} / ${pad(total)}`;
    if (vPrev) vPrev.disabled = i === 0;
    if (vNext) vNext.disabled = i === total - 1;
    load(i);
  };
  track.addEventListener('scroll', () => {
    if (vRaf) return;
    vRaf = requestAnimationFrame(() => { vRaf = 0; const i = indexOf(track, total); if (i !== vIdx) vPaint(i); });
  }, { passive: true });
  const vGo = (i) => jump(track, Math.max(0, Math.min(total - 1, i)), true);
  vPrev && vPrev.addEventListener('click', () => vGo(vIdx - 1));
  vNext && vNext.addEventListener('click', () => vGo(vIdx + 1));

  const open = (i) => {
    load(i);
    viewer.showModal();
    document.documentElement.style.overflow = 'hidden';
    jump(track, i, false);
    vPaint(i);
    // the phone's Back button closes the viewer instead of leaving the product
    try { history.pushState({ vsViewer: true }, ''); pushed = true; } catch (e) { pushed = false; }
  };
  const finish = () => {
    if (viewer.open) viewer.close();
    document.documentElement.style.overflow = '';
    go(vIdx, false); // main gallery follows the photo you ended on
  };
  const close = () => {
    if (pushed) { pushed = false; history.back(); } // popstate → finish()
    else finish();
  };
  window.addEventListener('popstate', () => { if (viewer.open) { pushed = false; finish(); } });
  viewer.addEventListener('cancel', (e) => { e.preventDefault(); close(); }); // Esc
  $('[data-viewer-close]', viewer).addEventListener('click', close);
  viewer.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') vGo(vIdx + 1);
    if (e.key === 'ArrowLeft') vGo(vIdx - 1);
  });

  // a tap (not a swipe) on a photo opens the viewer at that photo
  slider.addEventListener('click', (e) => {
    const z = e.target.closest('[data-open-viewer]');
    if (!z) return;
    open(+z.dataset.openViewer);
  });
}

function initTabs(root) {
  const box = $('[data-vs-tabs]', root);
  if (!box) return;
  const tabs = $$('[role="tab"]', box);
  const select = (tab, focus) => {
    tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
    if (focus) tab.focus();
  };
  tabs.forEach((t, i) => {
    t.addEventListener('click', () => select(t));
    t.addEventListener('keydown', (e) => {
      let n = -1;
      if (e.key === 'ArrowRight') n = (i + 1) % tabs.length;
      if (e.key === 'ArrowLeft') n = (i - 1 + tabs.length) % tabs.length;
      if (e.key === 'Home') n = 0;
      if (e.key === 'End') n = tabs.length - 1;
      if (n > -1) { e.preventDefault(); select(tabs[n], true); }
    });
  });
}

function initWish(root) {
  const btn = $('[data-vs-pdp-wish]', root);
  if (!btn) return;
  const text = $('[data-wish-text]', btn);
  const handle = btn.dataset.vsWish;
  const read = () => { try { return JSON.parse(localStorage.getItem('vs-wishlist') || '[]') || []; } catch (e) { return []; } };
  const paint = () => {
    const on = read().indexOf(handle) > -1;
    btn.setAttribute('aria-pressed', String(on));
    if (text) text.textContent = on ? 'Saved to Wishlist' : 'Add to Wishlist';
  };
  paint();
  document.addEventListener('vs:wishlist', paint); // vs-theme.js does the actual toggle
}

function init(root) {
  if (root.__vsPdp) return;
  root.__vsPdp = true;
  document.body.dataset.template = 'product';

  initGallery(root);
  initTabs(root);
  initWish(root);

  const form = $('[data-vs-form]', root);
  const idInput = $('[data-vs-id]', root);
  const atc = $('[data-vs-atc]', root);
  const atcText = $('[data-atc-text]', root);
  const err = $('[data-vs-error]', root);
  const sticky = $('[data-vs-sticky]', root);
  const stickyBtn = $('[data-vs-sticky-atc]', root);
  const stickyText = $('[data-sticky-text]', root);
  const stickyPrice = $('[data-sticky-price]', root);
  const soldLabel = root.dataset.soldLabel || 'Sold out';
  let variants = [];
  try { variants = JSON.parse($('[data-vs-variants]', root).textContent); } catch (e) {}
  const fieldsets = $$('.vs-pdp__opt', root);

  /* ---------- variants ---------- */
  const selected = () => fieldsets.map((f) => ($('input:checked', f) || {}).value);
  const current = () => {
    if (!fieldsets.length) return variants[0];
    const s = selected();
    return variants.find((v) => v.options.every((o, i) => o === s[i]));
  };

  function paintAvailability() {
    const s = selected();
    fieldsets.forEach((f, i) => {
      $$('.vs-pill', f).forEach((p) => {
        const val = $('input', p).value;
        const ok = variants.some((v) => v.available && v.options[i] === val && v.options.every((o, k) => k === i || o === s[k]));
        p.classList.toggle('is-oos', !ok);
      });
    });
  }

  function apply(first) {
    const v = current();
    paintAvailability();
    fieldsets.forEach((f) => { const c = $('input:checked', f); const l = $('[data-opt-label]', f); if (c && l) l.textContent = c.value; });
    if (!v) {
      if (atc) { atc.disabled = true; atcText.textContent = 'Unavailable'; }
      if (stickyBtn) { stickyBtn.disabled = true; stickyText.textContent = 'Unavailable'; }
      return;
    }
    idInput.value = v.id;
    const now = $('[data-price-now]', root), was = $('[data-price-was]', root), off = $('[data-price-off]', root);
    if (now) now.textContent = money(v.price);
    const sale = v.compare > v.price;
    if (was) { was.hidden = !sale; was.textContent = sale ? money(v.compare) : ''; }
    if (off) { off.hidden = !sale; off.textContent = sale ? Math.round(((v.compare - v.price) * 100) / v.compare) + '% Off' : ''; }
    if (stickyPrice) stickyPrice.textContent = money(v.price);

    const sold = !v.available || (v.tracked && v.qty <= 0);
    const label = sold ? soldLabel : 'Add to cart';
    [[atc, atcText], [stickyBtn, stickyText]].forEach(([b, t]) => { if (b) { b.disabled = sold; b.classList.remove('is-busy'); } if (t) t.textContent = label; });

    if (!first) {
      const url = new URL(location.href); url.searchParams.set('variant', v.id); history.replaceState(history.state, '', url);
      if (v.image && root.__vsGo) root.__vsGo(v.image);
    }
    document.dispatchEvent(new CustomEvent('variant:update'));
  }
  fieldsets.forEach((f) => f.addEventListener('change', () => apply(false)));
  apply(true);

  /* ---------- add to cart ---------- */
  let busy = false;
  const showError = (msg) => { if (!err) return; err.textContent = msg; err.hidden = false; setTimeout(() => (err.hidden = true), 8000); };

  async function add(e) {
    e && e.preventDefault();
    const v = current();
    if (busy || !v || !v.available || (v.tracked && v.qty <= 0)) return;
    busy = true; err && (err.hidden = true);
    [atc, stickyBtn].forEach((b) => b && b.classList.add('is-busy'));
    if (atcText) atcText.textContent = 'Adding…';
    if (stickyText) stickyText.textContent = 'Adding…';

    const fd = new FormData();
    fd.append('id', v.id); fd.append('quantity', '1'); fd.append('form_type', 'product'); fd.append('utf8', '✓');
    const sectionIds = [...new Set($$('cart-items-component').map((el) => el.dataset.sectionId).filter(Boolean))];
    if (sectionIds.length) fd.append('sections', sectionIds.join(','));

    try {
      const cfg = fetchConfig('javascript', { body: fd });
      const res = await fetch(Theme.routes.cart_add_url, { ...cfg, headers: { ...cfg.headers, Accept: 'text/html' } });
      const json = await res.json();
      if (json.status) {
        showError(json.description || json.message || 'Could not add this item.');
        flash('Add to cart', false);
      } else {
        const cart = await fetch((window.VS && window.VS.root ? window.VS.root.replace(/\/?$/, '/') : '/') + 'cart.js').then((r) => r.json()).catch(() => undefined);
        form.dispatchEvent(new CartAddEvent(cart, String(v.id), {
          source: 'product-form-component', itemCount: 1, productId: root.dataset.productId, sections: json.sections,
        }));
        flash('Added ✓', true);
      }
    } catch (x) {
      console.error(x); showError('Something went wrong. Please try again.'); flash('Add to cart', false);
    }
  }
  function flash(text, ok) {
    if (atcText) atcText.textContent = text;
    if (stickyText) stickyText.textContent = text;
    [atc, stickyBtn].forEach((b) => b && b.classList.remove('is-busy'));
    setTimeout(() => { busy = false; apply(true); }, ok ? 1500 : 200);
  }
  form.addEventListener('submit', add);
  stickyBtn && stickyBtn.addEventListener('click', () => form.requestSubmit());

  /* ---------- sticky bar (phones/tablets) ---------- */
  const buy = $('[data-vs-buy]', root);
  if (sticky && buy && 'IntersectionObserver' in window) {
    new IntersectionObserver(([en]) => {
      const show = !en.isIntersecting && en.boundingClientRect.top < 0;
      sticky.classList.toggle('is-on', show);
      sticky.setAttribute('aria-hidden', String(!show));
      if (stickyBtn) stickyBtn.tabIndex = show ? 0 : -1;
    }, { threshold: 0 }).observe(buy);
  }

  /* ---------- share ---------- */
  const share = $('[data-vs-share]', root);
  const shareText = share && $('[data-share-text]', share);
  share && share.addEventListener('click', async () => {
    const url = share.dataset.vsShare, title = share.dataset.title;
    try {
      if (navigator.share) await navigator.share({ title, url });
      else {
        await navigator.clipboard.writeText(url);
        if (shareText) { shareText.textContent = 'Link copied'; setTimeout(() => (shareText.textContent = 'Share'), 1600); }
      }
    } catch (x) { /* dismissed */ }
  });
}

const boot = () => $$('[data-vs-pdp]').forEach(init);
boot();
document.addEventListener('shopify:section:load', boot);
